/**
 * LoadingOverlay.tsx — Full-screen loading overlay component
 *
 * Displays a modal overlay with:
 * - Large spinner
 * - Progress bar
 * - Current status message
 * - User instructions
 */

import React from "react";
import {
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Body1,
  Subtitle2,
  Body1Strong,
} from "@fluentui/react-components";
import "./LoadingOverlay.css";

interface LoadingOverlayProps {
  statusMessage?: string;
  etaMessage?: string | null;
  progress?: { completed?: number; total?: number } | null;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ statusMessage, etaMessage, progress }) => {
  const hasDeterminateProgress = progress?.total != null && progress.total > 0;
  const progressValue = hasDeterminateProgress
    ? (progress.completed ?? 0) / progress.total!
    : undefined;
  return (
    <div
      className="loading-overlay-container"
    >
      <Spinner size="huge" labelPosition="below" label="Processing..." className="loading-overlay-processing-text"/>

      {/* Current Phase */}
      {statusMessage && (
        <div className="loading-overlay-status-group">
          <Subtitle2
            className="loading-overlay-status"
          >
            {statusMessage}
          </Subtitle2>

          {etaMessage && (
            <Body1Strong className="loading-overlay-eta">
              {etaMessage}
            </Body1Strong>
          )}
        </div>
      )}

      <Body1
        className="loading-overlay-instructions"
      >
          Styling assistant is processing this manuscript and should be done shortly.
          <br/>
          <br/>
          Please leave this pane open until processing is complete.
      </Body1>

      <MessageBar intent="info" shape="rounded" className="loading-overlay-message-bar">
        <MessageBarBody>
          <MessageBarTitle>Document Protected</MessageBarTitle>
          While processing, this document is protected. Once processing is complete, the document
          will be available for editing.
        </MessageBarBody>
      </MessageBar>
    </div>
  );
};
